import { createContext, useReducer, ReactNode, useEffect } from 'react';
import noop from 'lodash/noop';

import type {
  AddOverlayActionPayload,
  OverlayManagerContextValue,
  RemoveOverlayActionPayload,
  UpdateOverlayDimensionsPayload,
  UpdateOverlayVisibilityPayload,
} from './types';
import { defaultOverlayState } from './overlay-manager.constants';
import {
  calculateInset,
  overlayManagerReducer,
} from './overlay-manager.helpers';
import debounce from 'lodash/debounce';

const zeroNoop = () => 0;

const defaultValue: OverlayManagerContextValue = {
  add: noop,
  remove: noop,
  getInsetFor: zeroNoop,
  getInsetToClear: zeroNoop,
  getOverlay: () => null,
  updateDimensions: noop,
  updateVisibility: noop,
  getTotalInset: zeroNoop,
};

export const OverlayManagerContext =
  createContext<OverlayManagerContextValue>(defaultValue);

type OverlayManagerProps = {
  children: ReactNode;
};

export const OverlayManager = ({ children }: OverlayManagerProps) => {
  const [overlayState, dispatchOverlayAction] = useReducer(
    overlayManagerReducer,
    defaultOverlayState,
  );

  const add = (addInfo: AddOverlayActionPayload) => {
    dispatchOverlayAction({
      type: 'ADD_OVERLAY',
      payload: addInfo,
    });
  };

  const remove = (removeInfo: RemoveOverlayActionPayload) => {
    dispatchOverlayAction({
      type: 'REMOVE_OVERLAY',
      payload: removeInfo,
    });
  };

  const getInsetFor = (componentName: string) => {
    return calculateInset(componentName, overlayState.overlays);
  };

  const getInsetToClear = (componentName: string) => {
    const insetBelow = calculateInset(componentName, overlayState.overlays);
    const overlay = getOverlay(componentName);
    if (insetBelow != null && overlay && overlay.clientRect != null) {
      return insetBelow + overlay.clientRect.height;
    }
    return 0;
  };

  const getOverlay = (componentName: string) => {
    const overlay = overlayState.overlays.find(
      overlay => overlay.componentName === componentName,
    );

    return overlay || null;
  };

  const updateDimensions = (payload: UpdateOverlayDimensionsPayload) => {
    dispatchOverlayAction({
      type: 'UPDATE_OVERLAY_DIMENSIONS',
      payload,
    });
  };

  const updateVisibility = (payload: UpdateOverlayVisibilityPayload) => {
    dispatchOverlayAction({
      type: 'UPDATE_OVERLAY_VISIBILITY',
      payload,
    });
  };

  const getTotalInset = () => {
    return calculateInset(null, overlayState.overlays) || 0;
  };

  useEffect(() => {
    const onResize = () => {
      overlayState.overlays.forEach(overlay => {
        updateDimensions({
          componentName: overlay.componentName,
        });
      });
    };

    const debouncedResize = debounce(onResize, 250);

    window.addEventListener('resize', debouncedResize);

    return () => window.removeEventListener('resize', debouncedResize);
  }, [updateDimensions]);

  return (
    <OverlayManagerContext.Provider
      value={{
        add,
        remove,
        getInsetFor,
        getInsetToClear,
        getOverlay,
        updateDimensions,
        updateVisibility,
        getTotalInset,
      }}
    >
      {children}
    </OverlayManagerContext.Provider>
  );
};
